import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
    financialPaymentExcel,
    getFinancialPayments,
    updatePayment,
    uploadPayment,
} from "Services/Reporting";
import AccordionTable from "./components/AccordionTable";
import { Button, PageTemplate } from "components";
import styles from "./style.module.scss";
import { Toast } from "components";
import { ResponseHandler } from "components";
import { paymentItem, paymentObj } from "./types";
import { addCamma, convertGregorianDateToPersian } from "utils/helpers";
import ExtendRowTable from "components/ExtendRowTable/ExtendRowTable";
import { ReactComponent as ViewIcon } from "assets/icons/view-fill.svg";
import { ReactComponent as UploadIcon } from "assets/icons/cloud-upload.svg";
import { ReactComponent as Download } from "assets/icons/cloud-download.svg";
import { getObjectOfTable } from "./utils";

const Payments = () => {
    const { vendorID, periodID } = useParams<{
        vendorID: string;
        periodID: string;
    }>();
    const initialToast = {
        status: "success",
        show: false,
        message: "",
    };
    const [toast, setToast] = useState(initialToast);
    const inputElement = useRef<HTMLInputElement>(null);
    const [paymentsReport, setPaymentsReport] = useState({
        organizational: [],
        other: [],
        snappfood: [],
    });
    const { refetch, isFetching } = useQuery(
        ["financialPayments", periodID, vendorID],
        () => getFinancialPayments({ periodID, vendorID }),
        {
            onSuccess: (res) => {
                setPaymentsReport(res?.data);
                setToast(initialToast);
            },
        }
    );
    const { mutate: mutateExportExcel } = useMutation(
        ["financial-sale-excel"],
        financialPaymentExcel,
        {
            onSuccess: (res) => {
                const link = document.createElement("a");
                link.href = res.data;
                link.setAttribute("download", "financial-sale-excel");
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
            },
        }
    );
    const { mutate: updatePaymentReq, isLoading: isLoadingUpdatePayment } =
        useMutation(updatePayment, {
            onSuccess: () => {
                if (refetch) {
                    refetch();
                }
            },
        });
    const { mutate: uploadPaymentReq } = useMutation(uploadPayment, {
        onError: () => {},
    });

    const snappfoodData: Array<paymentObj> = getObjectOfTable(
        paymentsReport?.snappfood
    );
    const organizationalData: Array<paymentObj> = getObjectOfTable(
        paymentsReport?.organizational
    );
    const otherData: Array<paymentObj> = getObjectOfTable(
        paymentsReport.other,
        "other"
    );

    const renderSnappfoodData = useMemo(() => snappfoodData, [paymentsReport]);
    const renderOrganizationalData = useMemo(
        () => organizationalData,
        [paymentsReport]
    );
    const renderOtherData = useMemo(() => otherData, [paymentsReport]);

    const paymentReports = [
        {
            id: 0,
            title: "پرداخت‌های اسنپ‌فود",
            type: "snappfood",
            active: true,
            data: renderSnappfoodData,
        },
        {
            id: 1,
            title: "پرداخت‌های سازمانی",
            type: "organizational",
            active: false,
            data: renderOrganizationalData,
        },
        {
            id: 2,
            title: "پرداخت‌های دیگر",
            type: "other",
            active: false,
            data: renderOtherData,
        },
    ];
    const COLUMN = [
        {
            Header: "شعبه",
            accessor: "branch_title",
            enableRowSpan: true,
        },
        {
            Header: "تاریخ",
            accessor: "date",
            Cell: (props: any) =>
                convertGregorianDateToPersian(props.value).replaceAll("-", "/"),
        },
        {
            Header: "علت",
            accessor: "reason",
        },
        {
            Header: "مبلغ (ریال)",
            accessor: "amount",
            Cell: (props: any) => addCamma(props.value),
        },
        {
            Header: "عملیات",
            accessor: "filename",
            Cell: (props: any) => {
                return (
                    <div className={styles.tableActions}>
                        {props.row.original?.filename && (
                            <div
                                onClick={() =>
                                    handleView && handleView(props.row.original)
                                }
                            >
                                <ViewIcon />
                            </div>
                        )}

                        <div className={styles.uploadContainer}>
                            <input
                                type="file"
                                accept=".pdf"
                                ref={inputElement}
                                id="fileInput"
                                className={styles.uploadInput}
                                onChange={(e) => {
                                    e.preventDefault();
                                    if (!!e.target.files && e.target.files[0]) {
                                        handleUpload(e.target.files[0]);
                                    }
                                }}
                                onDrop={(acceptedFiles: any) =>
                                    handleDrop && handleDrop(acceptedFiles)
                                }
                            />
                            <label
                                htmlFor={`fileInput-${props.row.original.id}`}
                                className={styles.uploadIcon}
                            >
                                <UploadIcon
                                    onClick={() =>
                                        handleUploadItem(props.row.original)
                                    }
                                />
                            </label>
                        </div>
                    </div>
                );
            },
        },
    ];

    const handleView = (item: paymentItem) => {
        if (item.filename) {
            localStorage.setItem("filename", JSON.stringify(item.filename));
        }
        window.open("/financial-payment-factor", "_blank", "noreferrer");
    };
    const handleUploadItem = (data: paymentItem) => {
        inputElement.current?.click();
        localStorage.setItem("finance-row", JSON.stringify(data));
    };
    const handleUpload = (value: any) => {
        //@ts-ignore
        const item = JSON.parse(localStorage.getItem("finance-row"));
        const data = new FormData();
        data.append("file", value);
        uploadPaymentReq(
            { paymentID: item.payment_id, data: data },
            {
                onSuccess: (response) => {
                    if (response.status === 200) {
                        const filename = response.data.filename;

                        if (item?.type && item.type === "other") {
                            const { payment_id, type, branch_id, ...rest } =
                                item;
                            const body = {
                                ...rest,
                                filename: filename,
                                branch_id: item?.branch_id || 0,
                                period_id: Number(periodID),
                                id: item.payment_id,
                            };
                            updatePaymentReq(body, {
                                onSuccess: () => {
                                    setToast({
                                        status: "success",
                                        show: true,
                                        message: "فایل با موفقیت ارسال شد",
                                    });
                                    refetch();
                                },
                            });
                        } else {
                            setToast({
                                status: "success",
                                show: true,
                                message: "فایل با موفقیت ارسال شد",
                            });
                            refetch();
                        }
                    }
                },
            }
        );
    };
    const handleDrop = (file: any) => {
        setToast(initialToast);
        if (file.length === 0) {
            setToast({
                status: "danger",
                show: true,
                message: "فقط فایل با فرمت pdf قابل ارسال است.",
            });
        }
    };
    return (
        <>
            <ResponseHandler loading={isFetching || isLoadingUpdatePayment}>
                <PageTemplate
                    groupName="گزارشات"
                    title={"دوره مالی"}
                    listOfTitles={["پرداخت‌ها"]}
                    customStylePageTemplate={styles.customStylePageTemplate}
                >
                    <div className={styles.exportExcalBtn}>
                        <Button
                            btnText={"خروجی اکسل"}
                            className="is-green bordered half-height"
                            handleClick={() =>
                                mutateExportExcel({ periodID, vendorID })
                            }
                            btnIcon={<Download className="mr-2" />}
                        />
                    </div>
                    <>
                        {paymentReports.map((report) => {
                            return (
                                <AccordionTable
                                    report={report}
                                    key={report.id}
                                    tableComponent={
                                        <div className={styles.tableContainer}>
                                            <div
                                                className={styles.tableContent}
                                            >
                                                <ExtendRowTable
                                                    className={
                                                        styles.financialTable
                                                    }
                                                    columns={COLUMN}
                                                    data={report.data}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            );
                        })}
                    </>
                </PageTemplate>
            </ResponseHandler>
            <Toast
                className={toast.status}
                isShow={toast.show}
                title={toast.message}
            />
        </>
    );
};

export default Payments;
